import { useCallback, useEffect, useRef, useState } from "react";
import { cachedGet, invalidateDashboardCache, peekCachedGet } from "./useCachedGet";
import { COMMENTS_CHANGED_EVENT, notifyCommentsChanged } from "../components/comments/commentEvents";

/** One comment surfaced on a summary row — the ask, the ACK owed, or the caller's own open ask. */
export interface SummaryCommentRef {
    commentId: number;
    excerpt: string;
    /** The excerpt was cut — read the thread before acting on it. */
    truncated: boolean;
    authorName: string | null;
    createdAt: string | null;
    /** Who it is waiting on (only on `waitingOnOthersComment`). */
    awaitingName: string | null;
}

/** Raw record facts (enum values, ISO dates) — `pages/discussions/discussionFacts` labels them. */
export interface SummaryFacts {
    status: string | null;
    cancelled: boolean;
    dateFrom: string | null;
    dateTo: string | null;
    dueAt: string | null;
    amount: number | null;
    balance: number | null;
    currency: string | null;
    counterparty: string | null;
    kind: string | null;
}

/**
 * One row of `GET comments/unread-summary` — a record the caller is part of a
 * conversation on. Itinerary / leg comments roll up under their mission, so
 * `parentType` is never "itinerary" or "leg" here.
 *
 * The optional fields arrived later (2026-10-04); an older server omits them.
 */
export interface UnreadSummaryRow {
    parentType: string;
    parentId: number;
    unread: number;
    total: number;
    waitingOnMe: number;
    waitingOnOthers: number;
    pendingAcks: number;
    lastCommentAt: string | null;
    lastCommentId?: number | null;
    lastExcerpt: string | null;
    lastAuthorName: string | null;
    referenceNumber: string | null;
    title: string | null;
    /** Record page + `?drawer=comments` — navigate to it as-is. */
    actionUrl: string;
    /** The oldest open comment waiting on the caller. */
    waitingComment?: SummaryCommentRef | null;
    /** The oldest ack-required comment the caller has not acknowledged. */
    ackComment?: SummaryCommentRef | null;
    /** The oldest comment the caller wrote that still waits on someone else. */
    waitingOnOthersComment?: SummaryCommentRef | null;
    facts?: SummaryFacts | null;
}

/**
 * Fired by anything that changes comment state (posting, acking, marking a
 * thread read). Every discussions consumer — sidebar badge, dashboard widget,
 * Discussions page, list-row chips — refetches on it so counts never go stale
 * behind an open thread.
 */
export { COMMENTS_CHANGED_EVENT, notifyCommentsChanged };

/** No polling faster than this — the summary is a cross-schema query. */
const REFRESH_INTERVAL_MS = 60 * 1000;
const SUMMARY_URL_PREFIX = "comments/unread-summary";

export type DiscussionModule = "Missions" | "Requests" | "Leads" | "Tasks" | "Finance" | "CRM";

const MODULE_BY_PARENT_TYPE: Record<string, DiscussionModule> = {
    mission: "Missions",
    itinerary: "Missions",
    leg: "Missions",
    mission_request: "Requests",
    request: "Requests",
    lead: "Leads",
    task: "Tasks",
    invoice: "Finance",
    bill: "Finance",
    payment: "Finance",
    client: "CRM",
    company: "CRM",
    provider: "CRM",
    organization: "CRM",
};

/** Which sidebar module a comment parent belongs to (null for unknown types). */
export const moduleForParentType = (parentType: string): DiscussionModule | null =>
    MODULE_BY_PARENT_TYPE[(parentType || "").toLowerCase()] ?? null;

export interface DiscussionsSummaryState {
    rows: UnreadSummaryRow[];
    loading: boolean;
    /** Network failure or 5xx. */
    error: boolean;
    /** 403 — the caller may not use comments at all; render nothing. */
    forbidden: boolean;
    refresh: () => void;
}

/**
 * The caller's discussions, refreshed on mount, every 60s while the tab is
 * visible, and on `crx:comments-changed`.
 *
 * Goes through the shared TTL cache so the sidebar badge, the dashboard widget
 * and the Discussions page — all mounted at once on the dashboard — share ONE
 * request per URL instead of three. Explicit refreshes (timer, event) drop the
 * cached entry first so they always reach the network.
 * @param onlyUnread - Restrict to records with something unread (sidebar / widget).
 */
export function useDiscussionsSummary(onlyUnread: boolean): DiscussionsSummaryState {
    const url = `${SUMMARY_URL_PREFIX}?onlyUnread=${onlyUnread ? "true" : "false"}`;
    const [rows, setRows] = useState<UnreadSummaryRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [forbidden, setForbidden] = useState(false);
    const mountedRef = useRef(true);

    const load = useCallback(async (bypassCache: boolean) => {
        // Drop a RESOLVED entry so the refetch reaches the network; an entry
        // still in flight (another consumer just asked) is shared instead of
        // being replaced by a second identical request.
        if (bypassCache && peekCachedGet(url, REFRESH_INTERVAL_MS) !== null) {
            invalidateDashboardCache(url);
        }
        try {
            const response = await cachedGet<UnreadSummaryRow[]>(url, REFRESH_INTERVAL_MS);
            if (!mountedRef.current) return;
            if (response.status === 200) {
                setRows(Array.isArray(response.body) ? response.body : []);
                setError(false);
                setForbidden(false);
            } else if (response.status === 403) {
                setRows([]);
                setForbidden(true);
            } else {
                setError(true);
            }
        } catch {
            if (mountedRef.current) setError(true);
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, [url]);

    useEffect(() => {
        mountedRef.current = true;
        setLoading(true);
        load(false);

        const onChanged = () => load(true);
        window.addEventListener(COMMENTS_CHANGED_EVENT, onChanged);

        // Polls only while the tab is visible — a backgrounded tab hitting this
        // every minute for hours is exactly the cost the 60s floor is meant to cap.
        const timer = window.setInterval(() => {
            if (typeof document === "undefined" || document.visibilityState === "visible") {
                load(true);
            }
        }, REFRESH_INTERVAL_MS);

        return () => {
            mountedRef.current = false;
            window.removeEventListener(COMMENTS_CHANGED_EVENT, onChanged);
            window.clearInterval(timer);
        };
    }, [load]);

    const refresh = useCallback(() => { load(true); }, [load]);

    return { rows, loading, error, forbidden, refresh };
}

/**
 * Total unread comments across every discussion the caller is in — the number
 * on the sidebar's Discussions item. One cheap call, shared with the widget.
 */
export function useDiscussionsUnread(): number {
    const { rows } = useDiscussionsSummary(true);
    return rows.reduce((sum, row) => sum + (Number(row.unread) || 0), 0);
}
